import React, { useEffect, useRef, useState } from 'react';
import {
  TextField,
  ReferenceField,
  DateField,
  ListBase,
  ListToolbar,
  Pagination,
  Datagrid,
  Title,
  useListContext,
  useRefresh,
  useVersion,
} from 'react-admin';

import { Avatar, Card, CardContent, makeStyles } from '@material-ui/core';
import BlockIcon from '@material-ui/icons/Block';
import CheckCircleIcon from '@material-ui/icons/CheckCircle';
import DeleteOutlineIcon from '@material-ui/icons/DeleteOutline';
import HourglassEmptyIcon from '@material-ui/icons/HourglassEmpty';
import LocalShippingIcon from '@material-ui/icons/LocalShipping';
import MonetizationOnIcon from '@material-ui/icons/MonetizationOn';
import PrintIcon from '@material-ui/icons/Print';
import ReportProblemIcon from '@material-ui/icons/ReportProblem';
import VisibilityIcon from '@material-ui/icons/Visibility';
import Skeleton from '@material-ui/lab/Skeleton';
import { useSnackbar } from 'notistack';

import { DialogoConfirmacao } from '../../Components';
import ActionsField from '../../Components/ReactAdmin/Fields/ActionsField';
import { ProtegerPagina } from '../../layout/VerificaPermissaoPage';
import { api, imprimirNotaDescarregoMercadoria } from '../../services';
import { stringify, formatMoeda, formatPeso } from '../../utils';
import { PERMISSAO_TELA_DESCARREGO_MERCADORIA_LISTAR } from '../../utils/dadosTelas';
import CompraField from './CompraField';
import DialogoCreate from './DialogoCreate';
import DialogoShow from './DialogoShow';
import Filter from './Filter';
import FornecedorField from './FornecedorField';
import NfeField, { DetalhesExpand } from './NfeField';
import PagoField from './PagoField';
import PesoField from './PesoField';
import StatusField from './StatusField';
import ValorField from './ValorField';

const useStyles = makeStyles((theme) => ({
  card: {
    borderRadius: 20,
    overflow: 'hidden',
    // cabeçalho da tabela na cor primária do sistema
    '& thead th': {
      backgroundColor: theme.palette.primary.main,
      color: theme.palette.primary.contrastText,
      fontWeight: 'bold',
    },
    '& thead th .MuiTableSortLabel-root, & thead th .MuiTableSortLabel-root:hover': {
      color: theme.palette.primary.contrastText,
    },
    '& tbody tr:hover': { backgroundColor: theme.palette.action.hover },
  },
}));

const CardTotal = ({ label, valor, detalhe, cor, icon, loading }) => (
  <Card
    style={{
      borderRadius: 12,
      borderLeft: `5px solid ${cor}`,
      height: '100%',
    }}
  >
    <CardContent
      style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        padding: '8px 12px',
      }}
    >
      <div style={{ minWidth: 0 }}>
        <div style={{ fontSize: 12, color: '#737477', lineHeight: 1.3 }}>
          {label}
        </div>
        <div
          style={{
            fontSize: 18,
            fontWeight: 'bold',
            color: '#212121',
            lineHeight: 1.3,
          }}
        >
          {loading ? <Skeleton variant="text" width={100} /> : valor}
        </div>
        <div style={{ fontSize: 12, color: '#737477', lineHeight: 1.3 }}>
          {loading ? '\u00a0' : detalhe}
        </div>
      </div>
      <Avatar
        style={{
          backgroundColor: cor,
          color: 'white',
          width: 32,
          height: 32,
        }}
      >
        {icon}
      </Avatar>
    </CardContent>
  </Card>
);

const TOTAIS_VAZIO = {
  quantidade: 0,
  peso: 0,
  valor_cobrado: 0,
  pagos: { quantidade: 0, valor: 0 },
  pendentes: { quantidade: 0, valor: 0 },
  isentos: { quantidade: 0, valor_referencia: 0 },
  pago_nao_usado: { quantidade: 0, valor: 0 },
};

const ResumoTotais = () => {
  const { filterValues } = useListContext();
  const version = useVersion();
  const [totais, setTotais] = useState(TOTAIS_VAZIO);
  const [loading, setLoading] = useState(false);

  const {
    periodo,
    chave_acesso_nota_fiscal: chave,
    usado_em_compra: usado,
    fornecedor_id: fornecedor,
    pago,
  } = filterValues;

  useEffect(() => {
    let ativo = true;
    setLoading(true);
    api
      .get(
        `/descarrego_mercadorias/totais?${stringify({
          filter: JSON.stringify({
            periodo,
            chave_acesso_nota_fiscal: chave,
            usado_em_compra: usado,
            fornecedor_id: fornecedor,
            pago,
          }),
        })}`,
      )
      .then(({ data }) => ativo && setTotais(data))
      .finally(() => ativo && setLoading(false));
    return () => {
      ativo = false;
    };
  }, [periodo, chave, usado, fornecedor, pago, version]);

  const descarregos = (n) => `${n} descarrego${n === 1 ? '' : 's'}`;
  const cartoes = [
    {
      label: 'Quantidade',
      valor: totais.quantidade,
      detalhe: formatPeso(totais.peso),
      cor: '#009FD4',
      icon: <LocalShippingIcon />,
    },
    {
      label: 'Valor cobrado',
      valor: formatMoeda(totais.valor_cobrado),
      detalhe: 'Pagos + pendentes',
      cor: '#3f51b5',
      icon: <MonetizationOnIcon />,
    },
    {
      label: 'Pagos',
      valor: formatMoeda(totais.pagos.valor),
      detalhe: descarregos(totais.pagos.quantidade),
      cor: '#2e7d32',
      icon: <CheckCircleIcon />,
    },
    {
      label: 'Pendentes',
      valor: formatMoeda(totais.pendentes.valor),
      detalhe: descarregos(totais.pendentes.quantidade),
      cor: '#ed6c02',
      icon: <HourglassEmptyIcon />,
    },
    {
      label: 'Isentos',
      valor: descarregos(totais.isentos.quantidade),
      detalhe: `Ref.: ${formatMoeda(totais.isentos.valor_referencia)}`,
      cor: '#757575',
      icon: <BlockIcon />,
    },
    {
      label: 'Pago e não usado em compras',
      valor: formatMoeda(totais.pago_nao_usado.valor),
      detalhe: descarregos(totais.pago_nao_usado.quantidade),
      cor: '#c62828',
      icon: <ReportProblemIcon />,
    },
  ];

  return (
    <div
      style={{
        display: 'grid',
        // no máximo 4 cartões por linha
        gridTemplateColumns:
          'repeat(auto-fit, minmax(max(220px, calc(25% - 12px)), 1fr))',
        gap: 12,
        margin: '12px 0',
      }}
    >
      {cartoes.map((c) => (
        <CardTotal key={c.label} loading={loading} {...c} />
      ))}
    </div>
  );
};

const MyList = ({ ...props }) => {
  const classes = useStyles();
  const refDialogoShow = useRef(null);
  const refDialogoCreate = useRef(null);
  const refDialogoConfirmacao = useRef(null);
  const refresh = useRefresh();
  const { enqueueSnackbar } = useSnackbar();

  // só descarrego pendente pode ser excluído (o servidor recusa o pago); libera a vaga e a NF-e
  function handleExcluir(record) {
    if (refDialogoConfirmacao.current) {
      refDialogoConfirmacao.current.handleOpen(
        'Excluir descarrego',
        `Excluir o descarrego nº ${record.id}? A NF-e volta a ficar disponível para um novo descarrego.`,
        record.id,
      );
    }
  }

  async function handleConfirmaExclusao(id) {
    try {
      const { data } = await api.delete(`/descarrego_mercadorias/${id}`);
      if (data.status) {
        enqueueSnackbar('Descarrego excluído com sucesso!', {
          variant: 'success',
        });
      } else {
        enqueueSnackbar(`${data.message}`, { variant: 'error' });
      }
    } catch (e) {
      enqueueSnackbar('Erro ao excluir o descarrego!', { variant: 'error' });
    }
    refresh();
  }

  async function handleImprimir(record) {
    await imprimirNotaDescarregoMercadoria(record.id);
  }

  function handleShow(record) {
    if (refDialogoShow.current) {
      refDialogoShow.current.handleOpen(record.id);
    }
  }

  // só há um descarrego aguardando pagamento por vez: com um pendente, nem abre o diálogo
  async function handleNovo() {
    try {
      const { data } = await api.get('/descarrego_mercadorias/pendente');
      if (data.data) {
        enqueueSnackbar(
          `Já existe um descarrego aguardando pagamento (nº ${data.data.id}). Pague no caixa ou exclua antes de criar outro.`,
          { variant: 'error' },
        );
        return;
      }
    } catch (e) {
      enqueueSnackbar(
        'Não foi possível verificar se há descarrego aguardando pagamento.',
        { variant: 'error' },
      );
      return;
    }

    if (refDialogoCreate.current) refDialogoCreate.current.handleOpen();
  }

  // o botão de novo descarrego (ícone) fica ao lado do botão Filtros, dentro do Filter
  const filtros = React.cloneElement(props.filters, { onNovo: handleNovo });

  return (
    <>
      <Title title="Descarrego" />
      <ListToolbar {...props} filters={filtros} actions={false} />
      <ResumoTotais />
      <Card {...props} className={classes.card}>
        <Datagrid {...props} expand={<DetalhesExpand />}>
          <TextField source="id" label="ID" sortable={false} />
          <PagoField source="pago" label="Pago" sortable={false} />
          <CompraField source="uso_compra" label="Compra" sortable={false} />
          <NfeField source="nfes" label="NF-e" sortable={false} />
          <ReferenceField
            sortable={false}
            label="Fornecedor"
            source="fornecedor_id"
            reference="fornecedores"
            linkType={false}
          >
            <FornecedorField />
          </ReferenceField>
          <PesoField source="peso" label="Peso" sortable={false} />
          <ValorField source="valor" label="Valor Total" sortable={false} />
          <DateField source="data" label="Data" showTime sortable={false} />
          <StatusField source="isento" label="Isento" sortable={false} />
          <ActionsField
            source="lalala"
            textAlign="right"
            label="Ações"
            sortable={false}
            actions={[
              {
                key: 'delete',
                title: 'Excluir',
                icon: <DeleteOutlineIcon />,
                show: (rec) => !rec.pago,
                onClick: (rec) => handleExcluir(rec),
              },
              {
                key: 'show',
                title: 'Detalhes',
                icon: <VisibilityIcon />,
                onClick: (rec) => handleShow(rec),
              },
              {
                key: 'print',
                title: 'Imprimir',
                icon: <PrintIcon />,
                // só descarrego pago tem nota: pendente e isento não imprimem
                show: (rec) => rec.pago && !rec.isento,
                onClick: (rec) => handleImprimir(rec),
              },
            ]}
          />
        </Datagrid>
        <TransacoesPagination />
      </Card>
      <DialogoShow ref={refDialogoShow} handleClose={() => {}} />
      <DialogoCreate ref={refDialogoCreate} handleClose={refresh} />
      <DialogoConfirmacao
        ref={refDialogoConfirmacao}
        handleConfirma={handleConfirmaExclusao}
      />
    </>
  );
};

const ContainerMyList = ({ children, ...props }) => (
  <>
    <ListBase {...props}>
      <MyList {...props} />
    </ListBase>
  </>
);

const TransacoesPagination = (props) => (
  <Pagination rowsPerPageOptions={[5, 10, 25, 50, 100, 150, 200]} {...props} />
);

const List2 = (props) => (
  <ProtegerPagina permissao={PERMISSAO_TELA_DESCARREGO_MERCADORIA_LISTAR}>
    <ContainerMyList
      {...props}
      bulkActionButtons={false}
      pagination={<TransacoesPagination />}
      perPage={50}
      sort={{ field: 'data', order: 'DESC' }}
      filters={<Filter />}
      title="Descarrego de Mercadoria"
    />
  </ProtegerPagina>
);

export default List2;
